import { useState, useEffect, useMemo } from "react";
import { apiAxios } from "../../utils/axios";
import { Params, RequestName } from "./apiHookTypes";

const getEndpoint = (params: Params) => {
  switch (params.name) {
    case RequestName.CHECK_IP:
      // Check that queryIp exists and is not null
      if (params.checkIpParams?.queryIp) {
        let endpoint = `${RequestName.CHECK_IP}/${params.checkIpParams.queryIp}`;
        // Add mode parameter if it is specified
        if (params.checkIpParams.mode !== undefined) {
          endpoint += `?mode=${params.checkIpParams.mode}`;
        }
        return endpoint;
      }
      // Return null if IP is not specified to avoid making a request
      return null;
    case RequestName.GET_INVOICE:
      return `${params.name}/${params.getInvoiceParams.invoiceId}`;
    case RequestName.CREATE_INVOICE:
      return `${params.name}/${params.createInvoiceParams.network}/${params.createInvoiceParams.currency}/${params.createInvoiceParams.amount}`;
    case RequestName.BUY_SUBSCRIPTION:
      return `${params.name}/${params.buySubscriptionParams.subscriptionId}`;
    case RequestName.API_KEY_SUMMARY:
      // Describes a user key: without one there is nothing to ask about.
      return params.apiToken ? params.name : null;
    case RequestName.REFERRAL_TRANSFER:
      // Mutation: only fire once the component sets the submit trigger,
      // otherwise the POST would go out on mount.
      return params.referralTransferParams?.submit ? params.name : null;
    case RequestName.REFERRAL_PAYOUT_REQUEST:
      // Mutation: needs both fields before it may fire.
      return params.payoutRequestParams?.currency &&
        params.payoutRequestParams?.address
        ? params.name
        : null;
    default:
      return params.name;
  }
};

// `refreshKey`: bump the number to re-run the same GET (e.g. refresh stats
// right after a mutation instead of waiting for the next poll tick).
const useApiRequest = (
  params: Params,
  pollInterval: number | null = null,
  refreshKey: number = 0
) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const endpoint = getEndpoint(params);

  // Check that endpoint is valid (not null and does not contain null/undefined)
  const hasNullEndopoint = !endpoint || endpoint
    .split("/")
    .some((param) => param === "null" || param === "undefined");

  const apiRequestUrl = endpoint ? `/${endpoint}` : null;
  
  // Debug logging
  // if (params.name === RequestName.CHECK_IP) {
  //   console.log("🔍 [API_HOOK] Building URL:", apiRequestUrl);
  //   console.log("🔍 [API_HOOK] Parameters:", params.checkIpParams);
  // }

  const config = useMemo(
    () => {
      // If there is no valid URL, return null
      if (!apiRequestUrl) {
        return null;
      }
      
      const headers: any = {};
      
      // Add Authorization only if token exists
      if (params.apiToken && params.apiToken !== 'undefined') {
        headers.Authorization = `Bearer ${params.apiToken}`;
      }
      
      const baseConfig: any = {
        method: params.method,
        url: apiRequestUrl,
        headers,
      };

      // Add request body for UPDATE_INVOICE
      if (params.name === RequestName.UPDATE_INVOICE && params.updateInvoiceParams) {
        baseConfig.data = {
          invoice_id: params.updateInvoiceParams.invoiceId,
          network: params.updateInvoiceParams.network,
          coin: params.updateInvoiceParams.currency,
        };
      }

      // POST /referral/transfer takes an EMPTY body: the server always moves
      // the full available referral balance. NEVER send an amount from here.
      if (params.name === RequestName.REFERRAL_TRANSFER) {
        baseConfig.data = {};
      }

      // POST /referral/payout_request — currency + address only; every money
      // amount (gross/fee/net) is computed server-side.
      if (
        params.name === RequestName.REFERRAL_PAYOUT_REQUEST &&
        params.payoutRequestParams
      ) {
        baseConfig.data = {
          currency: params.payoutRequestParams.currency,
          address: params.payoutRequestParams.address,
        };
      }

      return baseConfig;
    },
    // Trigger/body objects must live in component state (stable identity):
    // a new object here re-runs the request effect below. referralTransferParams
    // is kept intentionally (defensive re-fire on a fresh trigger) even though
    // its effect currently also flows through apiRequestUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      params.method,
      apiRequestUrl,
      params.apiToken,
      params.name,
      params.updateInvoiceParams,
      params.referralTransferParams,
      params.payoutRequestParams,
    ]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiAxios(config);
        setData(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasNullEndopoint && config) {
      fetchData();

      if (pollInterval) {
        const intervalId = setInterval(fetchData, pollInterval);

        return () => clearInterval(intervalId);
      }
    }
  }, [config, hasNullEndopoint, pollInterval, apiRequestUrl, refreshKey]);

  return { data, error, loading };
};

export default useApiRequest;
